import { useState } from "react";
import { Link } from "react-router-dom";

interface CompareProduct {
  id: string;
  name: string;
  icon: string;
  category: string;
  purpose: string;
  bestFor: string[];
  keyFeatures: string[];
  integrations: string[];
}

interface FeatureMatrixRow {
  label: string;
  matches: string[];
  tooltips: Record<string, string>;
}

const products: CompareProduct[] = [
  {
    id: "worktrack",
    name: "WorkTrack",
    icon: "ri-map-pin-user-line",
    category: "Workforce Management",
    purpose: "Real-time team activity, task tracking, and field operations",
    bestFor: ["Field teams", "Remote workers", "Shift-based operations"],
    keyFeatures: ["GPS tracking", "Task assignment", "Daily reporting", "Ticketing", "Messaging"],
    integrations: ["HCM", "OMS", "RemoteOps Hub"],
  },
  {
    id: "hris",
    name: "HCM",
    icon: "ri-user-settings-line",
    category: "Human Resources",
    purpose: "Employee records, leave, attendance, and lifecycle management",
    bestFor: ["Multi-location enterprises", "Growing teams", "Regulated industries"],
    keyFeatures: ["Employee records", "Leave tracking", "Org chart", "Document storage", "Onboarding"],
    integrations: ["WorkTrack", "PMS", "Finance", "Admin"],
  },
  {
    id: "pms",
    name: "PMS",
    icon: "ri-bar-chart-grouped-line",
    category: "Performance Management",
    purpose: "KPIs, goal setting, reviews, and continuous performance tracking",
    bestFor: ["Sales orgs", "Service teams", "Manufacturing units"],
    keyFeatures: ["KPI dashboards", "360 feedback", "Scorecards", "Review cycles", "Trend analytics"],
    integrations: ["HCM", "WorkTrack", "Learn", "RemoteOps Hub"],
  },
  {
    id: "finance",
    name: "Accounting",
    icon: "ri-coins-line",
    category: "Revenue Management",
    purpose: "Invoicing, billing automation, receivables, and revenue dashboards",
    bestFor: ["Service businesses", "Logistics companies", "Agencies", "Professional services"],
    keyFeatures: ["Invoice generation", "Rate cards", "Auto billing", "Payment tracking", "Revenue dashboards"],
    integrations: ["WorkTrack", "OMS", "Admin", "HCM"],
  },
  {
    id: "admin",
    name: "Admin",
    icon: "ri-shield-user-line",
    category: "Internal Operations",
    purpose: "Facility, fleet, travel, procurement, asset, HSE, service desk, insurance, and administrative control",
    bestFor: ["Corporate offices", "Multi-site orgs", "Regulated workplaces"],
    keyFeatures: ["Facility management", "Fleet management", "Travel management", "Procurement management", "Asset management", "HSE", "Service desk", "Insurance management", "Approvals workflow", "Vendor management", "Document control", "Visitor tracking", "Audit logs"],
    integrations: ["WorkTrack", "HCM", "PMS", "Finance"],
  },
  {
    id: "quality-audit",
    name: "Quality Audit",
    icon: "ri-shield-check-line",
    category: "Compliance & Quality",
    purpose: "Audit planning, execution, findings, and compliance reporting",
    bestFor: ["Manufacturing", "Logistics", "Regulated industries", "ISO-compliant orgs"],
    keyFeatures: ["Audit scheduling", "Checklist builder", "Findings tracking", "Corrective actions", "Compliance reports"],
    integrations: ["OMS", "WorkTrack", "Admin", "RemoteOps Hub"],
  },
  {
    id: "learn",
    name: "Learning & Development",
    icon: "ri-graduation-cap-line",
    category: "Training & Knowledge",
    purpose: "Course creation, training programmes, assessments, and certifications",
    bestFor: ["Onboarding programmes", "Compliance training", "Sales training", "Leadership development"],
    keyFeatures: ["Course builder", "Progress tracking", "Quizzes", "Certificates", "Learning paths"],
    integrations: ["HCM", "PMS", "Admin"],
  },
  {
    id: "remoteops-hub",
    name: "RemoteOps Hub",
    icon: "ri-remote-control-line",
    category: "Control & Execution",
    purpose: "Centralised oversight for distributed teams, branches, and systems",
    bestFor: ["Retail chains", "Logistics networks", "Multi-site manufacturers"],
    keyFeatures: ["Multi-location dashboard", "Live ops feed", "Incident alerts", "Resource coordination", "Executive reports"],
    integrations: ["WorkTrack", "OMS", "Finance", "PMS", "HCM", "Quality Audit"],
  },
  {
    id: "growth-marketing",
    name: "Growth & Marketing",
    icon: "ri-megaphone-line",
    category: "Marketing & Demand Gen",
    purpose: "Drive awareness, generate demand, and execute campaigns that convert prospects",
    bestFor: ["Marketing teams", "Digital agencies", "Growth-stage startups"],
    keyFeatures: ["Campaign management", "Content planner", "Lead capture forms", "Email & SMS marketing", "AI content suggestions"],
    integrations: ["Sales & CRM", "HCM", "Learn"],
  },
  {
    id: "sales-crm",
    name: "Sales & CRM",
    icon: "ri-exchange-dollar-line",
    category: "Sales & Customer Relationships",
    purpose: "Manage your sales pipeline and build stronger customer relationships",
    bestFor: ["Sales teams", "Account managers", "B2B businesses"],
    keyFeatures: ["Lead management", "Pipeline tracking", "Quotations & proposals", "Sales dashboards", "Team collaboration"],
    integrations: ["Growth & Marketing", "Finance", "HCM", "WorkTrack"],
  },
  {
    id: "workplace-services",
    name: "Workplace Services",
    icon: "ri-building-4-line",
    category: "Internal Services & Facilities",
    purpose: "Streamline internal service requests and improve employee experience",
    bestFor: ["Corporate offices", "Facility teams", "Multi-site organisations"],
    keyFeatures: ["Service requests", "Facility maintenance", "Meeting room booking", "Visitor management", "Helpdesk & ticketing"],
    integrations: ["Admin", "HCM", "WorkTrack"],
  },
];

const featureMatrix: FeatureMatrixRow[] = [
  {
    label: "Workforce Tracking",
    matches: ["worktrack", "remoteops-hub"],
    tooltips: {
      worktrack: "GPS tracking, task assignments, God's Eye View dashboard",
      "remoteops-hub": "Multi-location dashboard, live ops feed, remote team oversight",
    },
  },
  {
    label: "HR & People",
    matches: ["hris", "pms", "learn"],
    tooltips: {
      hris: "Employee records, leave tracking, organisational hierarchy",
      pms: "KPI dashboards, 360 feedback, performance scorecards",
      learn: "Course builder, certifications, competency tracking",
    },
  },
  {
    label: "Finance & Billing",
    matches: ["finance"],
    tooltips: {
      finance: "Invoice generation, rate cards, auto billing, revenue dashboards",
    },
  },
  {
    label: "Quality & Audits",
    matches: ["quality-audit"],
    tooltips: {
      "quality-audit": "Audit scheduling, checklist builder, compliance reports, findings tracking",
    },
  },
  {
    label: "Operations Control",
    matches: ["admin", "remoteops-hub"],
    tooltips: {
      admin: "Request workflows, approval chains, facility management",
      "remoteops-hub": "Multi-location monitoring, incident alerts, resource coordination",
    },
  },
  {
    label: "Field / Mobile",
    matches: ["worktrack", "remoteops-hub", "quality-audit"],
    tooltips: {
      worktrack: "GPS tracking, mobile-first interface, offline sync",
      "remoteops-hub": "Remote team dashboards, location-based alerts",
      "quality-audit": "Mobile audit execution, photo capture, offline checklists",
    },
  },
  {
    label: "Reporting & Analytics",
    matches: ["pms", "remoteops-hub", "finance"],
    tooltips: {
      pms: "Scorecards, trend analytics, team comparison reports",
      "remoteops-hub": "Cross-location analytics, executive summary reports",
      finance: "Revenue dashboards, cash flow analytics, AR visibility",
    },
  },
  {
    label: "Marketing Campaigns",
    matches: ["growth-marketing"],
    tooltips: {
      "growth-marketing": "Campaign management, email & SMS marketing, AI-assisted content suggestions",
    },
  },
  {
    label: "Lead & Opportunity Tracking",
    matches: ["sales-crm"],
    tooltips: {
      "sales-crm": "Lead management, pipeline tracking, forecasting & reporting",
    },
  },
  {
    label: "Service Requests & Ticketing",
    matches: ["workplace-services", "admin"],
    tooltips: {
      "workplace-services": "Service request management, helpdesk & ticketing, meeting room booking",
      admin: "Service desk, visitor tracking, facility management",
    },
  },
];

function TooltipCell({
  hasFeature,
  tooltip,
}: {
  hasFeature: boolean;
  tooltip?: string;
}) {
  const [show, setShow] = useState(false);

  if (!hasFeature) {
    return (
      <div className="w-6 h-6 flex items-center justify-center rounded-full bg-graphite-800/60 border border-graphite-700/40 mx-auto">
        <span className="text-graphite-600 text-xs">N/A</span>
      </div>
    );
  }

  return (
    <div
      className="relative inline-block"
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
      onFocus={() => setShow(true)}
      onBlur={() => setShow(false)}
    >
      <div className="w-6 h-6 flex items-center justify-center rounded-full bg-emerald-accent/15 border border-emerald-accent/30 mx-auto cursor-help">
        <i className="ri-check-line text-emerald-accent text-xs" />
      </div>
      {tooltip && show && (
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 rounded-lg bg-graphite-800 border border-graphite-600 shadow-lg z-50 w-56">
          <p className="text-xs text-silver-200 leading-relaxed text-center">
            {tooltip}
          </p>
          <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-px">
            <div className="w-2 h-2 bg-graphite-800 border-r border-b border-graphite-600 rotate-45" />
          </div>
        </div>
      )}
    </div>
  );
}

export default function ProductComparisonTable() {
  const [viewMode, setViewMode] = useState<"overview" | "matrix">("overview");

  return (
    <section className="py-10 md:py-14">
      <div className="w-full px-4 sm:px-6 lg:px-8 xl:px-16">
        <div className="max-w-6xl mx-auto">
          <div className="mb-8 md:mb-10 text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-2">
              Compare All <span className="text-emerald-accent">11 BOS Modules</span>
            </h2>
            <p className="text-base text-silver-400 max-w-2xl mx-auto">
              See which StackMap apps match your operational needs at a glance.
            </p>
          </div>

          {/* View Toggle */}
          <div className="flex items-center justify-center mb-8">
            <div className="inline-flex items-center p-1 bg-graphite-800/60 rounded-lg border border-graphite-700/40">
              <button
                onClick={() => setViewMode("overview")}
                className={`px-4 py-2 rounded-md text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                  viewMode === "overview"
                    ? "bg-emerald-accent/15 text-emerald-accent"
                    : "text-silver-400 hover:text-silver-200"
                }`}
              >
                Product Overview
              </button>
              <button
                onClick={() => setViewMode("matrix")}
                className={`px-4 py-2 rounded-md text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                  viewMode === "matrix"
                    ? "bg-emerald-accent/15 text-emerald-accent"
                    : "text-silver-400 hover:text-silver-200"
                }`}
              >
                Feature Matrix
              </button>
            </div>
          </div>

          {viewMode === "overview" ? (
            <div className="overflow-x-auto -mx-4 px-4">
              <table className="w-full min-w-[900px] border-collapse">
                <thead>
                  <tr className="border-b border-graphite-700">
                    <th className="text-left text-xs font-semibold text-silver-400 uppercase tracking-wider py-3 pr-4 w-40">
                      Product
                    </th>
                    <th className="text-left text-xs font-semibold text-silver-400 uppercase tracking-wider py-3 pr-4 w-36">
                      Category
                    </th>
                    <th className="text-left text-xs font-semibold text-silver-400 uppercase tracking-wider py-3 pr-4 w-52">
                      Core Purpose
                    </th>
                    <th className="text-left text-xs font-semibold text-silver-400 uppercase tracking-wider py-3 pr-4 w-48">
                      Best For
                    </th>
                    <th className="text-left text-xs font-semibold text-silver-400 uppercase tracking-wider py-3 pr-4">
                      Key Capabilities
                    </th>
                    <th className="text-left text-xs font-semibold text-silver-400 uppercase tracking-wider py-3 w-24">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((p) => (
                    <tr
                      key={p.id}
                      className="border-b border-graphite-800 hover:bg-graphite-800/40 transition-colors"
                    >
                      <td className="py-4 pr-4">
                        <div className="flex items-center gap-2.5">
                          <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-emerald-accent/10 border border-emerald-accent/20 shrink-0">
                            <i className={`${p.icon} text-emerald-accent text-sm`} />
                          </div>
                          <span className="text-sm font-semibold text-white">{p.name}</span>
                        </div>
                      </td>
                      <td className="py-4 pr-4">
                        <span className="inline-block px-2 py-1 rounded-full bg-graphite-800 border border-graphite-700 text-xs text-silver-300">
                          {p.category}
                        </span>
                      </td>
                      <td className="py-4 pr-4">
                        <p className="text-sm text-silver-300 leading-relaxed">{p.purpose}</p>
                      </td>
                      <td className="py-4 pr-4">
                        <div className="flex flex-wrap gap-1">
                          {p.bestFor.map((b) => (
                            <span
                              key={b}
                              className="text-xs text-silver-400 bg-graphite-800/60 px-2 py-0.5 rounded"
                            >
                              {b}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="py-4 pr-4">
                        <div className="flex flex-wrap gap-1.5">
                          {p.keyFeatures.map((f) => (
                            <span
                              key={f}
                              className="inline-flex items-center gap-1 text-xs text-silver-300"
                            >
                              <span className="w-3.5 h-3.5 flex items-center justify-center rounded-full bg-emerald-accent/10 border border-emerald-accent/20 shrink-0">
                                <i className="ri-check-line text-emerald-accent text-[8px]" />
                              </span>
                              {f}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="py-4">
                        <Link
                          to={`/products/${p.id}`}
                          className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-accent hover:text-white transition-colors whitespace-nowrap"
                        >
                          Details
                          <span className="w-3.5 h-3.5 flex items-center justify-center">
                            <i className="ri-arrow-right-line text-xs" />
                          </span>
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="overflow-x-auto -mx-4 px-4">
              <table className="w-full min-w-[900px] border-collapse">
                <thead>
                  <tr className="border-b border-graphite-700">
                    <th className="text-left text-xs font-semibold text-silver-400 uppercase tracking-wider py-3 pr-4 w-48">
                      Capability
                    </th>
                    {products.map((p) => (
                      <th key={p.id} className="text-center py-3 px-2 min-w-[80px]">
                        <div className="flex flex-col items-center gap-1.5">
                          <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-emerald-accent/10 border border-emerald-accent/20">
                            <i className={`${p.icon} text-emerald-accent text-sm`} />
                          </div>
                          <span className="text-[11px] font-semibold text-white leading-tight">
                            {p.name}
                          </span>
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {featureMatrix.map((row) => (
                    <tr
                      key={row.label}
                      className="border-b border-graphite-800 hover:bg-graphite-800/40 transition-colors"
                    >
                      <td className="py-3.5 pr-4">
                        <span className="text-sm font-medium text-silver-200">{row.label}</span>
                      </td>
                      {products.map((p) => {
                        const hasFeature = row.matches.includes(p.id);
                        return (
                          <td key={p.id} className="py-3.5 px-2 text-center">
                            <TooltipCell
                              hasFeature={hasFeature}
                              tooltip={row.tooltips[p.id]}
                            />
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Mobile card view (shows below md breakpoint via CSS) */}
          <div className="md:hidden space-y-4 mt-2">
            {products.map((p) => (
              <div key={p.id} className="glass-card p-5">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-emerald-accent/10 border border-emerald-accent/20">
                    <i className={`${p.icon} text-emerald-accent text-lg`} />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-white">{p.name}</h3>
                    <span className="text-xs text-silver-400">{p.category}</span>
                  </div>
                </div>
                <p className="text-sm text-silver-300 leading-relaxed mb-3">{p.purpose}</p>
                <div className="flex flex-wrap gap-1 mb-3">
                  {p.bestFor.map((b) => (
                    <span
                      key={b}
                      className="text-xs text-silver-400 bg-graphite-800/60 px-2 py-0.5 rounded"
                    >
                      {b}
                    </span>
                  ))}
                </div>
                <div className="flex flex-wrap gap-x-3 gap-y-1 mb-3">
                  {p.keyFeatures.map((f) => (
                    <span key={f} className="inline-flex items-center gap-1 text-xs text-silver-300">
                      <i className="ri-check-line text-emerald-accent text-[10px]" />
                      {f}
                    </span>
                  ))}
                </div>
                <Link
                  to={`/products/${p.id}`}
                  className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-accent hover:text-white transition-colors"
                >
                  Learn more
                  <span className="w-3.5 h-3.5 flex items-center justify-center">
                    <i className="ri-arrow-right-line text-xs" />
                  </span>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
